import React, { ReactElement, useContext } from 'react';
import styled, { useTheme } from 'styled-components';

import { useMenu } from 'features/menu';
import { FullscreenContext } from 'features/fullscreen';

import { Box, ButtonIcon, ConditionalLink, Flex, Typography } from 'components';
import { createUrl, stripTags } from 'app/utils';
import { Link, useParams } from 'react-router-dom';
import { useRecommendations } from 'features/recommendations';
import { useProducts } from 'features/products';
import formatDateFromUtcToTimezone from 'app/utils/formatDateFromUtcToTimezone';

export const TitleWrapper = styled(Flex)`
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  align-items: center;

  :before {
    content: '';
    display: block;
    height: 100%;
    position: absolute;
    top: 0;
    right: 0;
    background: rgb(255, 255, 255);
    background: linear-gradient(80deg, rgba(255, 255, 255, 0) 0%, ${(props) => props.theme.colors.dark_150} 100%);
  }

  ${({ theme: { mediaQueries } }) => {
    return `
      :before {
        width: 50px;
      }

      ${mediaQueries.desktop} {
        :before {
          width: 70px;
        }
      }
    `;
  }}
`;

const RecommendationNavbar = (): ReactElement => {
  const { setMenuOpened } = useMenu();
  const { currentTheme, toggleTheme } = useTheme();
  const { toggleFullscreen, isFullscreen } = useContext(FullscreenContext);
  const { productSlug, productClassUuid } = useParams();

  const { productDetails } = useProducts();
  const { recommendationsDetails } = useRecommendations();

  return (
    <>
      <Flex
        bg="dark_150"
        flexWrap="nowrap"
        flexDirection={{ m: 'column', d: 'row' }}
        borderBottomColor="gray_600"
        borderBottomStyle="solid"
        borderBottomWidth="1px"
        as="header"
      >
        <Flex flexWrap="nowrap" borderBottomColor="gray_600" borderBottomStyle="solid" borderBottomWidth={{ m: '1px', d: 0 }}>
          <ButtonIcon
            width={{ m: 60, d: 80 }}
            height={{ m: 60, d: 80 }}
            justifyContent="center"
            icon="menu"
            onClick={() => setMenuOpened(true)}
            size="large"
            bg="transparent"
            color="white"
            colorHover="primary_400"
            colorClick="primary_200"
            aria-label="Abrir menu"
          />

          <Box position="relative" borderLeftWidth="1px" borderLeftStyle="solid" borderLeftColor="gray_600">
            <TitleWrapper>
              <Flex flexDirection="column" pl={{ m: 20, t: 30 }}>
                <Typography as="h2" type="type1" upper color="white">
                  {productDetails?.product?.name} • {formatDateFromUtcToTimezone(recommendationsDetails?.createdAt).format('DD/MM/YYYY')}
                </Typography>

                <Typography mt={{ m: 1, d: 2 }} as="h1" type="type5" fontWeight="bold" color="white">
                  {stripTags(recommendationsDetails?.title)}
                </Typography>
              </Flex>
            </TitleWrapper>
          </Box>
        </Flex>

        <Flex flexGrow={0}>
          <ButtonIcon
            width={{ m: 60, d: 80 }}
            height={{ m: 60, d: 80 }}
            justifyContent="center"
            icon={currentTheme === 'light' ? 'themeLight' : 'themeDark'}
            onClick={toggleTheme}
            size="large"
            flexShrink={0}
            bg="transparent"
            bgHover="dark_350"
            color="white"
            borderLeftWidth={1}
            borderLeftStyle="solid"
            borderLeftColor="gray_600"
            title={currentTheme === 'light' ? 'Ver tema escuro' : 'ver tema claro'}
          />

          <Link
            to={productClassUuid ? createUrl('productClassDetails', [productSlug, productClassUuid]) : createUrl('productDetails', [productSlug])}
          >
            <ButtonIcon
              width={{ m: 60, d: 80 }}
              height={{ m: 60, d: 80 }}
              justifyContent="center"
              icon="dashboard"
              onClick={() => setMenuOpened(false)}
              size="large"
              flexShrink={0}
              bg="transparent"
              bgHover="dark_350"
              color="white"
              borderLeftWidth={1}
              borderLeftStyle="solid"
              borderLeftColor="gray_600"
              title="Ir para o curso"
            />
          </Link>

          <Box flexGrow={0} display={{ m: 'none', d: 'block' }}>
            <ButtonIcon
              width={{ m: 60, d: 80 }}
              height={{ m: 60, d: 80 }}
              justifyContent="center"
              icon={isFullscreen ? 'fullscreenOff' : 'fullscreenOn'}
              onClick={toggleFullscreen}
              size="large"
              flexShrink={0}
              bg="transparent"
              bgHover="dark_350"
              color="white"
              borderLeftWidth={1}
              borderLeftStyle="solid"
              borderLeftColor="gray_600"
              title={isFullscreen ? 'Minimizar' : 'Maximizar'}
            />
          </Box>

          {Boolean(recommendationsDetails?.previousRecommendationUuid) && (
            <ConditionalLink
              to={createUrl('recommendations/details', [productSlug, productClassUuid, recommendationsDetails?.previousRecommendationUuid])}
              type="internal"
            >
              <ButtonIcon
                width={{ m: 60, d: 80 }}
                height={{ m: 60, d: 80 }}
                justifyContent="center"
                icon="chevronLeft"
                onClick={() => setMenuOpened(false)}
                size="large"
                flexShrink={0}
                bg="transparent"
                bgHover="dark_350"
                color="white"
                borderLeftWidth={1}
                borderLeftStyle="solid"
                borderLeftColor="gray_600"
                title={`Recomendação: ${stripTags(recommendationsDetails?.previousRecommendationTitle)}`}
                style={{ whiteSpace: 'pre' }}
              />
            </ConditionalLink>
          )}

          {Boolean(recommendationsDetails?.nextRecommendationUuid) && (
            <ConditionalLink
              to={createUrl('recommendations/details', [productSlug, productClassUuid, recommendationsDetails?.nextRecommendationUuid])}
              type="internal"
            >
              <ButtonIcon
                width={{ m: 60, d: 80 }}
                height={{ m: 60, d: 80 }}
                justifyContent="center"
                icon="chevronRight"
                onClick={() => setMenuOpened(false)}
                size="large"
                flexShrink={0}
                bg="transparent"
                bgHover="dark_350"
                color="white"
                borderLeftWidth={1}
                borderLeftStyle="solid"
                borderLeftColor="gray_600"
                borderRightWidth={1}
                borderRightStyle="solid"
                borderRightColor="gray_600"
                title={`Recomendação: ${stripTags(recommendationsDetails?.nextRecommendationTitle)}`}
              />
            </ConditionalLink>
          )}
        </Flex>
      </Flex>
    </>
  );
};
export default RecommendationNavbar;
